import Head from "next/head";
import Image from "next/image";
import NextLink from "next/link";
import PageLayout from "layouts/Page";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Icon,
  HStack,
  Divider,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { Truck, RotateCcw, ChevronRight } from "react-feather";
import { useState } from "react";
import { PRODUCTS } from "./index";

export default function ProductPage({ product }) {
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  if (!product) return null;

  const handleBuy = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/shop/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId: product.priceId,
          productName: product.name,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      toast({
        title: "Une erreur est survenue",
        description: "Veuillez réessayer ou nous contacter.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <Head>
        <title>{product.name} — Boutique Tête en l'Air</title>
        <meta name="description" content={product.description} />
      </Head>

      <Box pt={12} pb={20}>
        <Breadcrumb
          spacing="8px"
          separator={<Icon as={ChevronRight} color="gray.400" boxSize={3} />}
          mb={8}
          fontSize="sm"
          color="gray.500"
          fontFamily="Avenir"
        >
          <BreadcrumbItem>
            <NextLink href="/shop" passHref>
              <BreadcrumbLink color="brand.main">Boutique</BreadcrumbLink>
            </NextLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="gray.600">{product.name}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 8, md: 14 }}
          align="flex-start"
        >
          {/* Image */}
          <Box
            flex="1"
            w="100%"
            borderRadius="xl"
            overflow="hidden"
            bg="gray.100"
            boxShadow="xl"
            position="relative"
            minH="380px"
          >
            <Image
              src={product.image}
              alt={product.name}
              layout="fill"
              objectFit="cover"
            />
          </Box>

          {/* Infos produit */}
          <Box
            flex="1"
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            p={8}
          >
            <Heading
              as="h1"
              fontSize="2xl"
              mb={2}
              color="secondary.main"
              fontFamily="Avenir"
            >
              {product.name}
            </Heading>

            <Text
              fontSize="3xl"
              fontWeight="bold"
              color="brand.main"
              mb={4}
              fontFamily="Avenir"
            >
              {product.price}
            </Text>

            <Text color="gray.600" mb={6} lineHeight="tall" fontFamily="Avenir">
              {product.description}
            </Text>

            <Button
              size="lg"
              colorScheme="brand"
              bgColor="brand.main"
              color="white"
              _hover={{ bgColor: "brand.400" }}
              w="100%"
              onClick={handleBuy}
              isLoading={loading}
              loadingText="Redirection..."
              mb={6}
              fontFamily="Avenir"
            >
              Commander
            </Button>

            <Divider mb={5} />

            <Flex direction="column" gap={3}>
              <HStack spacing={3} color="brand.main">
                <Icon as={Truck} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold" fontFamily="Avenir">
                  Livraison offerte en France Métropolitaine
                </Text>
              </HStack>
              <HStack spacing={3} color="gray.500">
                <Icon as={Truck} boxSize={4} />
                <Text fontSize="sm" fontFamily="Avenir">
                  Livraison Hors France Métropolitaine : 5,00 €
                </Text>
              </HStack>
              <HStack spacing={3} color="gray.500">
                <Icon as={RotateCcw} boxSize={4} />
                <Text fontSize="sm" fontFamily="Avenir">
                  Garantie satisfait ou remboursé 30 jours
                </Text>
              </HStack>
              <HStack spacing={3} color="gray.500">
                <Icon as={Truck} boxSize={4} />
                <Text fontSize="sm" fontFamily="Avenir">
                  Expédition : 5–7 jours ouvrables
                </Text>
              </HStack>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </PageLayout>
  );
}

export async function getStaticPaths() {
  const paths = PRODUCTS.map((p) => ({ params: { slug: p.slug } }));
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const product = PRODUCTS.find((p) => p.slug === params.slug) || null;
  return { props: { product } };
}
